// Fechamento de um PEDIDO (retirada do Delivery) com as mesmas peças do
// Fechar mesa. Diferenças para a mesa: o total é o do pedido (pode ter taxa ou
// desconto já dados), nada vai pro caixa até a última parte, e o que falta é
// sempre "total − pago − descontos dados" — conta em centavos.
import { desfazerPagamento, efeitoNosItens, novoIdPagamento } from "./desfazer";
import {
  aplicarParte,
  criarDivisao,
  dividirValor,
  pagarSelecionados,
  pagarValorNosSelecionados,
  restanteItem,
  valorItem,
  valorSelecionado,
} from "./divisao";
import { editarParte } from "./partes";
import { nomeFormaAgora } from "../../pedidos/lib/formasDaLoja";

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const reais = (c) => Math.round(c) / 100;
const soma = (lista, f) => reais(lista.reduce((s, x) => s + centavos(f(x)), 0));

/**
 * Itens do pedido no formato do popup. A diferença entre o total do pedido e
 * a soma dos itens vira linha de "Taxa e acréscimos" (positiva) ou desconto
 * já dado no pedido (negativa), que é consumido nas primeiras rodadas.
 */
export function itensDoPedido(pedido) {
  const brutos = Array.isArray(pedido?.items) ? pedido.items : [];
  const itens = brutos.map((it, idx) => ({
    cartItemId: `p${idx}`,
    _idx: idx,
    // Quilo/litro (30/09/2026): o produto do item fica junto — sem ele a tela não
    // achava a medida no cadastro (mostrava "1.25x") e o produto a peso era pago
    // por unidade ("1 de 2" em 2 kg). sale_unit/is_divisible vêm do item ou do
    // cadastro (hooks/useFechamentoPedido completa).
    id: it.id || it.product_id || null,
    product_id: it.product_id || it.id || null,
    ...(it.sale_unit != null ? { sale_unit: it.sale_unit } : {}),
    ...(it.is_divisible != null ? { is_divisible: !!it.is_divisible } : {}),
    name: it.nome || it.name || "Item",
    final_price: Number(it.preco ?? it.price ?? it.final_price ?? 0),
    quantity: Number(it.quantidade ?? it.quantity ?? 1) || 1,
    photo_url: it.photo_url || null,
    observation: it.observacao || it.observation || null,
    subitems: it.subitems || [],
    combo_type: it.combo_type || null,
  }));
  const diferenca = reais(centavos(pedido?.total) - centavos(soma(itens, valorItem)));
  if (diferenca > 0.009) {
    itens.push({ cartItemId: "taxa", _idx: itens.length, name: Number(pedido?.valor_entrega) > 0 ? "Taxa de entrega" : "Taxa e acréscimos", final_price: diferenca, quantity: 1 });
  }
  return { itens, descontoPedido: diferenca < -0.009 ? -diferenca : 0 };
}

export const contaInicial = (itens) => ({ items: itens, log: [], divisao: null, descPedUsado: 0, descontos: [] });

/**
 * Pedido reaberto em Vendas → Pedidos: os pagamentos do fechamento de antes
 * voltam na lista, cada um "por valor" e com a lixeira. Nada disso está no
 * caixa (a reabertura estornou) — entra de novo quando o pedido for concluído.
 * O desconto que já estava no pedido conta como usado.
 */
export function contaReaberta(pedido, { itens, descontoPedido }) {
  const dividido = pedido?.forma_pagamento === "multiplo" && Array.isArray(pedido.pagamentos) && pedido.pagamentos.length;
  const partes = dividido
    ? pedido.pagamentos.map((p) => ({ code: p.method || p.code, label: p.label || nomeFormaAgora(p.method || p.code), value: Number(p.amount ?? p.valor ?? 0) }))
    : [{ code: pedido?.forma_pagamento, label: pedido?.pagamento_partes?.[0]?.nome || nomeFormaAgora(pedido?.forma_pagamento), value: Number(pedido?.total) || 0 }];
  const log = partes.filter((p) => p.code && p.value > 0.009).map((p) => ({
    ...p,
    value: reais(centavos(p.value)),
    id: novoIdPagamento(),
    timestamp: pedido.reaberto_em,
    reaberto: true,
    efeito: { itens: {}, porValor: reais(centavos(p.value)), divisaoId: null, concluiu: false, descPed: 0 },
  }));
  return { ...contaInicial(itens), log, descPedUsado: descontoPedido || 0 };
}

function descontoManual(base, desconto) {
  const v = Number(desconto?.valor) || 0;
  if (desconto?.tipo === "percent") return reais(centavos(base * Math.min(100, Math.max(0, v)) / 100));
  if (desconto?.tipo === "fixed") return Math.min(base, v);
  if (desconto?.tipo === "coupon") return Math.min(base, Number(desconto?.cupom?.discount) || 0);
  return 0;
}

export function situacao(conta, { total, descontoPedido }) {
  const pago = soma(conta.log, (p) => p.value);
  const descDado = soma(conta.descontos, (d) => d.valor);
  return {
    pago,
    restante: Math.max(0, reais(centavos(total) - centavos(pago) - centavos(descDado))),
    descPedRestante: Math.max(0, reais(centavos(descontoPedido) - centavos(conta.descPedUsado))),
  };
}

/**
 * O que cobrar agora: `valor` (líquido), `subtotal` (antes dos descontos),
 * `descPed` (fatia do desconto do pedido) e `descManual` (desconto da rodada).
 */
export function rodada(conta, { total, descontoPedido, desconto }) {
  const { restante, descPedRestante } = situacao(conta, { total, descontoPedido });
  const div = conta.divisao;
  if (div) {
    const k = div.pagas;
    const valor = div.valores[k] || 0;
    const descPed = div.descPedPartes[k] || 0;
    const descManual = div.descManualPartes[k] || 0;
    return { valor, subtotal: reais(centavos(valor) + centavos(descPed) + centavos(descManual)), descPed, descManual };
  }
  const selecionado = soma(conta.items, valorSelecionado);
  const descPed = selecionado > 0 ? Math.min(descPedRestante, selecionado) : descPedRestante;
  const base = selecionado > 0 ? Math.min(reais(centavos(selecionado) - centavos(descPed)), restante) : restante;
  const descManual = descontoManual(base, desconto);
  return { valor: Math.max(0, reais(centavos(base) - centavos(descManual))), subtotal: reais(centavos(base) + centavos(descPed)), descPed, descManual };
}

// Reparte `valor` nas partes na proporção de cada parte (sobra na última)
function ratear(valor, partes, liquido) {
  let acumulado = 0;
  return partes.map((p, i) => {
    const c = i === partes.length - 1 ? centavos(valor) - acumulado : Math.round(centavos(valor) * centavos(p) / Math.max(1, centavos(liquido)));
    acumulado += c;
    return reais(c);
  });
}

export function dividir(conta, { total, descontoPedido, desconto, partes }) {
  const r = rodada(conta, { total, descontoPedido, desconto });
  if (!(r.valor > 0)) return null;
  const selecionados = conta.items.filter((i) => i._selected && !i._paid);
  const mesaInteira = selecionados.length === 0;
  const itens = mesaInteira ? conta.items.filter((i) => !i._paid) : selecionados;
  const bruto = mesaInteira ? soma(itens, restanteItem) : soma(itens, valorSelecionado);
  const div = criarDivisao({ itens, bruto, liquido: r.valor, partes, mesaInteira, desconto: null });
  const valores = dividirValor(r.valor, partes);
  return {
    ...conta,
    items: conta.items.map((i) => ({ ...i, _selected: false, _selQtd: null })),
    divisao: {
      ...div,
      valores,
      descPedPartes: ratear(r.descPed, valores, r.valor),
      descManualPartes: ratear(r.descManual, valores, r.valor),
      // Só a marca de "com desconto" no quadro da divisão
      desconto: r.descPed + r.descManual > 0 ? { valor: reais(centavos(r.descPed) + centavos(r.descManual)) } : null,
      descontoMeta: r.descManual > 0 ? { tipo: desconto.tipo, percentual: desconto.tipo === "percent" ? Number(desconto.valor) : null, cupom: desconto.cupom?.code || null } : null,
    },
  };
}

/**
 * Registra um pagamento (uma ou mais formas). Devolve a conta nova e se ela
 * quitou. Quitou = não falta nada: todos os produtos ficam pagos.
 */
export function registrar(conta, pagamentos, { total, descontoPedido, desconto }) {
  const r = rodada(conta, { total, descontoPedido, desconto });
  const valorPago = soma(pagamentos, (p) => p.value);
  const forma = pagamentos.map((p) => p.label).join(" + ");
  const formaCompleta = pagamentos.map((p) => `${p.label}: R$ ${Number(p.value).toFixed(2).replace(".", ",")}`).join(" + ");
  const div = conta.divisao;
  const parte = div ? `${div.pagas + 1}/${div.partes}` : null;
  const pagamentoId = novoIdPagamento();
  const temSelecao = conta.items.some((i) => i._selected && !i._paid);
  // Rodada que não cobre o calculado (por valor, ou valor digitado com
  // produtos marcados) não consome os descontos
  const consumiu = div || valorPago >= r.valor - 0.009;
  let items = conta.items;
  let divisao = div;
  // Pedaço digitado com desconto em %: leva o desconto proporcional (pagar em
  // duas vezes não pode sair mais caro). Com desconto do próprio pedido
  // (cupom do cardápio) fica como antes.
  const pctPedaco = !div && temSelecao && !consumiu && desconto?.tipo === "percent" && r.descManual > 0 && !(r.descPed > 0) && r.valor > 0;
  const descPedaco = pctPedaco ? reais(centavos(valorPago * (r.subtotal / r.valor - 1))) : 0;
  if (div) ({ items, divisao } = aplicarParte(conta.items, div, forma, pagamentoId));
  else if (temSelecao && !consumiu) items = pagarValorNosSelecionados(conta.items, reais(centavos(valorPago) + centavos(descPedaco)), { forma, formaCompleta, pagamentoId });
  else if (temSelecao) items = pagarSelecionados(conta.items, { forma, formaCompleta, pagamentoId });
  const meta = div ? div.descontoMeta : (r.descManual > 0 ? { tipo: desconto.tipo, percentual: desconto.tipo === "percent" ? Number(desconto.valor) : null, cupom: desconto.cupom?.code || null } : null);
  const descManual = consumiu && r.descManual > 0 ? r.descManual : 0;
  const descPed = consumiu ? r.descPed : 0;
  const nova = {
    ...conta,
    items,
    divisao,
    descPedUsado: reais(centavos(conta.descPedUsado) + centavos(descPed)),
    descontos: descManual > 0 || descPedaco > 0
      ? [...conta.descontos, { valor: descManual || descPedaco, ...(meta || { tipo: "percent", percentual: Number(desconto.valor), cupom: null }), pagamento: pagamentoId }]
      : conta.descontos,
  };
  nova.log = [...conta.log, ...pagamentos.map((p) => ({ ...p, id: pagamentoId, ...(parte ? { parte } : {}), timestamp: new Date().toISOString() }))];
  const quitou = situacao(nova, { total, descontoPedido }).restante <= 0.009;
  if (quitou) nova.items = nova.items.map((i) => (i._paid ? i : { ...i, _paid: true, _selected: false, _paymentInfo: formaCompleta }));
  // O que este pagamento fez — é o que a lixeira desfaz
  const efeito = {
    itens: efeitoNosItens(conta.items, nova.items),
    porValor: 0,
    divisaoId: div?.id || null,
    concluiu: !!div && !divisao,
    ...(div && !divisao ? { divisaoAntes: div } : {}),
    descPed,
  };
  if (!(div && !div.id)) nova.log[conta.log.length] = { ...nova.log[conta.log.length], efeito };
  return { conta: nova, quitou, parte };
}

/** Apaga um pagamento da retirada: produtos, divisão e descontos voltam como estavam. */
export function desfazerNaConta(conta, id) {
  const r = desfazerPagamento(conta, id);
  if (r.erro) return r;
  return {
    conta: {
      ...conta,
      items: r.items,
      log: r.log,
      divisao: r.divisao,
      descPedUsado: Math.max(0, reais(centavos(conta.descPedUsado) - centavos(r.efeito.descPed))),
      descontos: conta.descontos.filter((d) => d.pagamento !== id),
    },
    entradas: r.entradas,
  };
}

/** Muda o valor de uma parte; os descontos das partes que faltam são repartidos de novo. */
export function editarParteNaConta(conta, indice, valor) {
  const div = conta.divisao;
  const r = editarParte(div, indice, valor);
  if (r.erro) return r;
  const nova = r.divisao;
  const pagas = div.pagas;
  const faltam = nova.valores.slice(pagas);
  const somaFaltam = soma(faltam, (v) => v);
  const refazer = (lista) => {
    const pago = (lista || []).slice(0, pagas);
    const resto = reais(centavos(soma(lista || [], (v) => v)) - centavos(soma(pago, (v) => v)));
    return [...pago, ...ratear(resto, faltam, somaFaltam)];
  };
  return { conta: { ...conta, divisao: { ...nova, descPedPartes: refazer(div.descPedPartes), descManualPartes: refazer(div.descManualPartes) } } };
}

/** Campos do PUT do pedido ao concluir: forma única ou "multiplo" + partes. */
export function fechamentoDoPedido(conta, pedido) {
  const porForma = new Map();
  conta.log.forEach((p) => {
    const atual = porForma.get(p.code) || { method: p.code, label: p.label, amount: 0 };
    atual.amount = reais(centavos(atual.amount) + centavos(p.value));
    porForma.set(p.code, atual);
  });
  const partes = [...porForma.values()];
  const payload = {
    forma_pagamento: partes.length === 1 ? partes[0].method : "multiplo",
    total: soma(conta.log, (p) => p.value),
  };
  if (partes.length > 1) payload.pagamentos = partes;
  const descTotal = soma(conta.descontos, (d) => d.valor);
  if (descTotal > 0) {
    const ultimo = conta.descontos[conta.descontos.length - 1];
    if (ultimo.tipo === "coupon" && !pedido.cupom_codigo && conta.descontos.every((d) => d.tipo === "coupon")) {
      payload.cupom_codigo = String(ultimo.cupom || "").toUpperCase();
      payload.cupom_desconto = descTotal;
    } else {
      payload.desconto_manual = reais(centavos(pedido.desconto_manual) + centavos(descTotal));
      payload.desconto_tipo = ultimo.tipo === "percent" ? "percent" : "fixed";
      if (ultimo.tipo === "percent") payload.desconto_percentual = ultimo.percentual;
    }
  }
  return payload;
}
